import React, { useMemo } from 'react';

import CardDialog from '~/components/CardDialog';
import WithConfirm from '~/components/ConfirmModal';
import { ContactEntity } from '~/components/Contact';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import { useCurrencyFormatter } from '~/components/CurrencyFormatterContext';
import { EyeIcon, TrashIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import { isoDateToUs } from '~/DateTimeUtils';
import type { CoveredObjectsResponseCoveredObjectsItem as CoveredObjectRow } from '~/server_shared/openapi/data-contracts';
import { COUNTRY_TO_STATE_MAP } from '~/Types';

const ACTION_COLUMN_ID = '__actions__';
const DISPLAY_NAME_COLUMN_ID = '__display_name__';
const EM_DASH = '—';

type FieldOption = { id: string; desc?: string };
type FieldConfig = {
  id: string;
  desc?: string;
  type?: string;
  options?: FieldOption[];
};

type CurrencyFormatter = { format: (n: number) => string };

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === '';

const stringifyOrEmpty = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const lookupOptionLabel = (options: FieldOption[] | undefined, value: unknown): string => {
  if (!Array.isArray(options)) return stringifyOrEmpty(value);
  const match = options.find((opt) => opt?.id === value);
  return match?.desc ?? stringifyOrEmpty(value);
};

const getByPath = <T = unknown,>(obj: unknown, path: string): T | undefined =>
  path
    .split('.')
    .reduce<unknown>(
      (acc, key) => (acc === null || acc === undefined ? undefined : (acc as Record<string, unknown>)[key]),
      obj
    ) as T | undefined;

// Pull the cell value for a default-field column off the v1 covered-object row.
// Default fields differ between auto and non-auto:
//   - auto:    getAutoFieldsConfig produces dotted ids like 'vehicle.year', 'vehicle.vin'
//              — resolve them against the row root so 'vehicle.year' becomes row.vehicle.year.
//   - non-auto: field configs are 'display_name' and 'contact_id' — values live on the row root.
const extractDefaultValue = (fieldConfig: FieldConfig, row: CoveredObjectRow, isAutoClaim: boolean): unknown => {
  if (!row || !fieldConfig?.id) return undefined;
  if (isAutoClaim) {
    return getByPath(row, fieldConfig.id);
  }
  if (fieldConfig.id === 'display_name') return row.display_name;
  if (fieldConfig.id === 'contact_id') return row.contact?.id ?? row.contact_id;
  return undefined;
};

// Render a covered-object cell value using the field config. Contact-typed
// fields delegate to ContactEntity (the standard icon + name + popover used
// across the codebase — ContactsTab, ContactSearch, InvolvedPersonHoverInfo)
// so the cell gets click-to-view + permission gating for free.
const renderFieldValue = ({
  config,
  value,
  row,
  currencyFormatter,
}: {
  config: FieldConfig;
  value: unknown;
  row: CoveredObjectRow;
  currencyFormatter: CurrencyFormatter;
}): React.ReactNode => {
  if (isEmpty(value)) return EM_DASH;

  if (config?.type === 'contact') {
    // fieldConfig.id points at the *_contact_id column; the resolved contact
    // object lives next to it at the same path minus the trailing _id, so we
    // can pass ContactEntity a displayName and skip its on-mount fetch.
    const contactPath = config.id?.replace(/_id$/, '');
    const contact =
      (contactPath ? getByPath<{ id?: number | string; full_name?: string }>(row, contactPath) : null) ?? null;
    const contactId = value ?? contact?.id ?? row?.contact?.id ?? row?.contact_id;
    if (!contactId) return EM_DASH;
    const displayName = contact?.full_name ?? row?.contact?.full_name ?? row?.contact_full_name;
    return <ContactEntity contactId={contactId} contactDisplayName={displayName} maxWidth="150px" />;
  }

  switch (config?.type) {
    case 'date':
      return isoDateToUs(value as string) ?? EM_DASH;
    case 'monetary': {
      const amount = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(amount) ? currencyFormatter.format(amount) : String(value);
    }
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'select':
      return lookupOptionLabel(config.options, value);
    case 'us_state_select': {
      const stateMap = COUNTRY_TO_STATE_MAP?.['US'] as Record<string, string> | undefined;
      const stateLabel = stateMap?.[value as string];
      return stateLabel ?? String(value);
    }
    case 'multiselect':
      return Array.isArray(value)
        ? value.map((v) => lookupOptionLabel(config.options, v)).join(', ')
        : lookupOptionLabel(config.options, value);
    case 'yes_no_na_unknown':
      return lookupOptionLabel(config.options, value);
    default:
      return String(value);
  }
};

type ColumnDef = {
  id: string;
  label: string;
  disableSort?: boolean;
  disableRowSelectionEvent?: boolean;
  rightPaddingOnly?: boolean;
  specialCell: (row: CoveredObjectRow) => React.ReactNode;
};

type BuildColumnsArgs = {
  defaultFieldConfigs?: FieldConfig[];
  isAutoClaim: boolean;
  currencyFormatter: CurrencyFormatter;
  isManualPolicy: boolean;
  onView?: (row: CoveredObjectRow) => void;
  onDelete?: (row: CoveredObjectRow) => void;
};

const buildColumns = ({
  defaultFieldConfigs,
  isAutoClaim,
  currencyFormatter,
  isManualPolicy,
  onView,
  onDelete,
}: BuildColumnsArgs): ColumnDef[] => {
  const fieldColumns: ColumnDef[] = (defaultFieldConfigs ?? []).map((fieldConfig) => ({
    id: fieldConfig.id,
    label: fieldConfig.desc ?? fieldConfig.id,
    disableSort: true,
    // eslint-disable-next-line react/display-name
    specialCell: (row: CoveredObjectRow) => {
      const value = extractDefaultValue(fieldConfig, row, isAutoClaim);
      const rendered = renderFieldValue({ config: fieldConfig, value, row, currencyFormatter });
      if (typeof rendered !== 'string') return rendered;
      return <OverflowTextWithToolTip maxWidth={160}>{rendered}</OverflowTextWithToolTip>;
    },
  }));

  const writeAction = isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY;

  return [
    {
      id: DISPLAY_NAME_COLUMN_ID,
      label: 'Display Name',
      disableSort: true,
      // eslint-disable-next-line react/display-name
      specialCell: (row: CoveredObjectRow) => (
        <span data-testid={`display-name-${row.id}`}>
          {row.display_name ?? row.vehicle?.display_name ?? '—'}
          {row.is_first_party && (
            <Chip
              size="small"
              label="First Party"
              className="ml-12 font-normal"
              data-testid={`first-party-chip-${row.id}`}
            />
          )}
        </span>
      ),
    },
    ...fieldColumns,
    {
      id: ACTION_COLUMN_ID,
      label: 'Actions',
      disableSort: true,
      disableRowSelectionEvent: true,
      rightPaddingOnly: true,
      // eslint-disable-next-line react/display-name
      specialCell: (row: CoveredObjectRow) => (
        <div className="flex items-center justify-end gap-12">
          <InlineIconButton
            icon={EyeIcon}
            tooltipTitle="View"
            onClick={() => onView?.(row)}
            data-testid={`view-action-${row.id}`}
          />
          <RestrictedPermissions action={writeAction} verb={PERMISSION_VERBS.WRITE}>
            <WithConfirm
              title={`Remove ${row.display_name ?? row.vehicle?.display_name ?? 'this covered object'} from the policy?`}
              contentText="Are you sure?"
              primaryButtonName="Yes, Remove"
              shouldCloseOnPrimary
            >
              <InlineIconButton
                icon={TrashIcon}
                tooltipTitle="Delete"
                onClick={() => onDelete?.(row)}
                data-testid={`delete-action-${row.id}`}
              />
            </WithConfirm>
          </RestrictedPermissions>
        </div>
      ),
    },
  ];
};

const ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50];

const EMPTY_FIELD_CONFIGS: FieldConfig[] = [];

interface CoveredObjectsTableProps {
  typeLabel: string;
  items: CoveredObjectRow[];
  totalCount: number;
  // Stable unfiltered total reported up from useCoveredObjectsQuery (gated on
  // appliedSearch === ''). Used for the "X of Y" header while filtering.
  unfilteredCount?: number | null;
  page: number;
  perPage: number;
  isLoading?: boolean;
  isError?: boolean;
  isManualPolicy?: boolean;
  isAutoClaim?: boolean;
  // True when the user has an active search (>= MIN_CHARS). Keeps the empty
  // card visible ("No matching covered objects.") instead of returning null.
  isSearchActive?: boolean;
  defaultFieldConfigs?: FieldConfig[];
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  onView?: (row: CoveredObjectRow) => void;
  onDelete?: (row: CoveredObjectRow) => void;
}

const CoveredObjectsTable: React.FC<CoveredObjectsTableProps> = ({
  typeLabel,
  items,
  totalCount,
  unfilteredCount = null,
  page,
  perPage,
  isLoading = false,
  isError = false,
  isManualPolicy = false,
  isAutoClaim = false,
  isSearchActive = false,
  defaultFieldConfigs = EMPTY_FIELD_CONFIGS,
  onPageChange,
  onPerPageChange,
  onView,
  onDelete,
}) => {
  const { currencyFormatter } = useCurrencyFormatter();

  const columns = useMemo(
    () => buildColumns({ defaultFieldConfigs, isAutoClaim, currencyFormatter, isManualPolicy, onView, onDelete }),
    [defaultFieldConfigs, isAutoClaim, currencyFormatter, isManualPolicy, onView, onDelete]
  );

  // While searching, surface the unfiltered total so the user knows how many
  // rows they're filtering against ("3 of 12"); otherwise just show the count.
  const title =
    isSearchActive && unfilteredCount !== null && unfilteredCount !== undefined
      ? `${typeLabel} (${totalCount} of ${unfilteredCount})`
      : `${typeLabel} (${totalCount})`;

  // Surface a load failure before the empty-return guard — otherwise a
  // request that fails before any successful response (totalCount === 0 +
  // isError) silently renders nothing instead of the failure card.
  if (isError) {
    return (
      <CardDialog title={title} data-testid={`covered-objects-table-${typeLabel}`}>
        <div role="alert">Failed to load. Retry by reloading the tab.</div>
      </CardDialog>
    );
  }

  // When searching we keep the empty card visible so the user sees "no
  // results". Outside search the connected wrapper already short-circuits
  // empty types so this branch is mostly a safety net.
  if (totalCount === 0 && !isLoading && !isSearchActive) {
    return null;
  }

  if (isLoading && items.length === 0) {
    return (
      <CardDialog title={title} data-testid={`covered-objects-table-${typeLabel}`}>
        <SkeletonTable rowsCount={5} columnsCount={Math.max(1, (defaultFieldConfigs?.length ?? 0) + 2)} />
      </CardDialog>
    );
  }

  if (totalCount === 0 && !isLoading) {
    return (
      <CardDialog title={title} data-testid={`covered-objects-table-${typeLabel}`}>
        <div className="p-12 text-center text-slate-600" data-testid={`covered-objects-empty-${typeLabel}`}>
          No matching covered objects.
        </div>
      </CardDialog>
    );
  }

  // SortableTable expects 0-indexed page; useCoveredObjectsQuery uses 1-indexed.
  const paginationProps = {
    page: Math.max(0, page - 1),
    count: totalCount,
    rowsPerPage: perPage,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    onChangePage: (_event: unknown, newPage: number) => onPageChange?.(newPage + 1),
    onChangeRowsPerPage: onPerPageChange
      ? (event: React.ChangeEvent<HTMLInputElement>) => onPerPageChange(parseInt(event.target.value, 10))
      : undefined,
  };

  return (
    <CardDialog title={title} data-testid={`covered-objects-table-${typeLabel}`}>
      <SortableTable
        columns={columns}
        rows={items}
        keepRowsOrder
        disableSortByUser
        stickyHeader
        paginationProps={paginationProps}
      />
    </CardDialog>
  );
};

export default CoveredObjectsTable;
